<template>
    <b-modal
            :id="modalId"
            :title="title"
            :visible="show"
            @hidden="hideEvents"
            class="modal-body-sm"
            body-class="pt-0"
            scrollable
    >
        <div class="sticky-top bg-white pt-3">
            <div class="d-flex justify-content-around">
                <h6>1. Add property</h6>
                <h6>2. Add compliance</h6>
                <h6>3. Add contact</h6>
            </div>
            <b-progress
                    :value="progress"
                    height="4px"
                    variant="primary"
            >
            </b-progress>
        </div>
        <vue-element-loading :active="loading" :is-full-screen="false"/>
        <r-form
                :disabled="false"
                :show-footer="false"
                :show-header="false"
                :validators="property.validators"
                ref="propertyForm"
                v-if="step === property.step"
                v-model="property.form"
        >
            One of the following fields is also required
            <ul>
                <li>Number range</li>
                <li>Building name</li>
                <li>Street number</li>
            </ul>
        </r-form>
        <r-form
                :disabled="false"
                :show-footer="false"
                :show-header="false"
                :validators="assessmentTypes.validators"
                class="assessmentTypesForm"
                ref="assessmentTypesForm"
                v-if="step === assessmentTypes.step && loadAssessmentTypesForm"
                v-model="assessmentTypes.form"
        >
        </r-form>
        <r-form
                :show-footer="false"
                :show-header="false"
                :validators="contact.validators"
                :disabled="contact.disabled"
                ref="contactForm"
                v-show="step === contact.step"
                v-model="contact.form"
        >
        </r-form>
        <b-button size="sm"
                  @click="contact.form.propertyContactId.value = null"
                  v-if="contact.disabled"
                  variant="yellow"
        >
            Create a new contact
            <font-awesome-icon
                    class="ml-2"
                    :icon="['far', 'pencil']"
                    size="sm"
            >
            </font-awesome-icon>
        </b-button>
        <template slot="modal-footer">
            <b-button
                    v-if="step === property.step || step === assessmentTypes.step"
                    @click="hideModal"
                    variant="outline-secondary">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                      @click="onClickSaveAssessmentTypes"
                      class="btn-arrow"
                      v-if="step === assessmentTypes.step"
                      variant="primary"
            >
                Save compliance
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    :disabled="loading"
                    @click="onClickAddNewProperty"
                    class="btn-arrow"
                    v-if="step === property.step"
                    variant="primary"
            >
                Add new property
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
            <div class="d-flex justify-content-between w-100"
                 v-if="step === contact.step">
                <b-button

                        @click="hideModal"
                        class="mr-2"
                        variant="yellow">
                    Skip
                    <font-awesome-icon
                            class="ml-2"
                            rotation="90"
                            :icon="['fal', 'redo']">
                    </font-awesome-icon>
                </b-button>
                <b-button
                        :disabled="loading"
                        @click="addContact"
                        class="btn-arrow"
                        variant="primary"
                >
                    {{ contact.buttonText }}
                    <font-awesome-icon
                            :icon="['far', 'arrow-right']">
                    </font-awesome-icon>
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import CaretIcon from '@/components/Form/CaretIcon';
import ModalMixin from '@/components/Modal/ModalMixin';
import FormValidate from '@/services/Mixins/FormValidate';
import RForm from '@/components/Form/RiskhubForm';
import Property from '@/services/Property/Property';
import AssessmentType from '@/services/Assessment/AssessmentType';
import PropertyAssessmentTypeStatus from '@/services/Property/PropertyAssessmentTypeStatus';
import { forEach, isObject, isEmpty, isUndefined } from 'lodash';
import { form as PropertyForm, validators as PropertyValidators } from '@/services/Property/PropertyForm';
import { form as PropertyStatusForm, validators as PropertyStatusValidators } from '@/services/Property/PropertyStatusForm';
import { form as PropertyAssessmentTypeForm } from '@/services/Property/PropertyAssessmentTypeForm';
import { form as PropertyContactForm, validators as PropertyContactValidators } from
  '@/services/Property/PropertyContactForm';
import PropertyContact from '@/services/Property/PropertyContact';
import ErrorMixin from '@/components/Notifications/ErrorMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';

export default {
  name: 'Add-Property-Modal',
  components: {
    CaretIcon,
    RForm,
  },
  mixins: [ModalMixin, FormValidate, ErrorMixin],
  data() {
    return {
      modalId: 'addPropertyModal',
      loading: false,
      options: [],
      step: 1,
      property: {
        step: 1,
        title: 'Add New Property',
        form: PropertyForm(),
        validators: PropertyValidators(),
        propertyId: null,
        uprn: null,
        valid: false,
        params: {},
      },
      assessmentTypes: {
        step: 2,
        title: 'Add Property compliance',
        formLoaded: false,
        form: {},
        validators: PropertyStatusValidators,
        propertyId: null,
        valid: false,
      },
      contact: {
        step: 3,
        title: 'Add new property contact',
        form: PropertyContactForm(),
        validators: PropertyContactValidators,
        disabled: false,
        buttonText: 'Add new contact to property',
        valid: false,
      },
      statusOptions: [],
    };
  },
  computed: {
    disabled() {
      return false;
    },
    progress() {
      return (95 / 3) * this.step;
    },
    title() {
      if (this.step >= this.contact.step) {
        return this.contact.title;
      }

      if (this.step >= this.assessmentTypes.step) {
        return this.assessmentTypes.title;
      }

      return this.property.title;
    },
    loadAssessmentTypesForm() {
      if (this.assessmentTypes.formLoaded) {
        return true;
      }
      this.getAssessmentTypesForm();
      return this.assessmentTypes.formLoaded;
    },
  },
  watch: {
    'contact.form.propertyContactId.value': {
      handler(newValue) {
        /**
         *  Do not change any values while loading.
         * This is useful for when we save the property details and go back
         * to the "disabled" property form
         */
        if (this.loading) {
          return;
        }

        if (!newValue) {
          this.contact.buttonText = 'Add new contact to property';
          this.contact.disabled = false;
          this.resetForm(this.contact.form);
          return;
        }

        const { propertyContactId } = newValue;
        if (!propertyContactId) {
          return;
        }

        this.populateFields(this.contact.form, newValue);
        this.contact.buttonText = 'Add contact to property';
        this.contact.disabled = true;
      },
    },
  },
  methods: {
    onClickAddNewProperty() {
      this.setProperty();
    },
    onClickSaveAssessmentTypes() {
      this.addProperty();
    },
    async getAssessmentTypesForm() {
      const formTemplate = PropertyAssessmentTypeForm();

      const assessmentTypesList = await AssessmentType.list();
      const assessmentTypes = assessmentTypesList.data.data;

      const statusList = await PropertyAssessmentTypeStatus.list();
      this.statusOptions = statusList.data.data;

      //const { data } = await AssessmentType.list();

      let assessmentTypesForm = PropertyStatusForm();
      forEach(assessmentTypes, (item) => {
        let assessmentTypeKeyPrefix = 'assessmentTypes[' + item.assessmentTypeId + ']';
        forEach(formTemplate, (value, key) => {
          let formItem = _.cloneDeep(value);
          formItem.label = value.label + ' for ' + item.assessmentType + ' Assessments';
          formItem.columnOptions.md = 12;
          let formItemKey = assessmentTypeKeyPrefix + '[' + key + ']';

          if (formItem.trackBy === 'propertyAssessmentTypeStatusId') {
            formItem.options = this.statusOptions;
          }

          assessmentTypesForm[formItemKey] = formItem;

          if (key === 'statusComment') {
            const statusField = formItemKey.replace('statusComment', 'propertyAssessmentTypeStatusId');
            this.assessmentTypes.validators[formItemKey] = {
              requiredIf: requiredIf(function() {
                if (isUndefined(assessmentTypesForm[statusField].value) || isEmpty(assessmentTypesForm[statusField].value)) {
                  return false;
                }
                return assessmentTypesForm[statusField].value.requiresComment;
              }),
            };
          }
        });
      });
      // Populate property status field
      this.$set(assessmentTypesForm['propertyStatusId'], 'value', 1);
      this.$set(assessmentTypesForm['programme'], 'value', {
        programme: 'REG10 Door Programme',
      });

      this.assessmentTypes.formLoaded = true;
      this.assessmentTypes.form = assessmentTypesForm;
    },

    async setProperty() {
      if (!this.$refs.propertyForm.isValidated()) {
        return;
      }

      this.loading = true;

      const params = {};

      forEach(this.property.form, (object, key) => {
        if (object.value.length > 0) {
          params[key] = object.value;
        }

        // If the type is a select, we want to set the value to be the key
        // because the value is an object, but most likely the original form
        // wants the key value.
        if (isObject(object.value)) {
            params[key] = object.value[object.trackBy];
        }
      });

      try {
        this.property.params = params;
        this.nextStep();
      } catch (e) {

      } finally {
        this.loading = false;
      }
    },
    async addProperty() {

      if (!this.$refs.assessmentTypesForm.isValidated()) {
        return;
      }

      this.loading = true;

      const assessmentTypes = {};

      forEach(this.assessmentTypes.form, (object, field) => {

        let { value } = object;

        if (value === null) {
          return true;
        }

        if (field.includes('assessmentTypes')) {
          let fieldNameParts = field.replace(/\]/g, '').split('[');
          assessmentTypes[fieldNameParts[1]] = assessmentTypes[fieldNameParts[1]] || {};
          if (typeof value === 'object') {
            value = value[fieldNameParts[2]];
          }
          assessmentTypes[fieldNameParts[1]][fieldNameParts[2]] = value;

        } else {
          if (typeof value === 'object') {
            value = value[field];
          }
          this.property.params[field] = value;
        }
        this.property.params.assessmentTypes = assessmentTypes;
      });

      try {
        const { data } = await Property.add(this.property.params);
        this.property.propertyId = data.data.propertyId;
        this.property.uprn = data.data.uprn;
        const title = 'New property added';
        const text = `UPRN - ${data.data.uprn} - Property successfully added`;
        const type = 'success';

        this.$notify({
          duration: 7000,
          title,
          text,
          type,
        });
        this.$emit('refresh-all');
        this.nextStep();
      } catch (e) {
        this.showValidationError(e);
      } finally {
        this.loading = false;
      }
    },
    async addContact() {
      // We need a propertyId to continue
      if (!this.property.propertyId) {
        this.step = 1;
        return false;
      }

      // We have a property contact selected
      if (this.contact.disabled) {
        const { propertyContactId } = this.contact.form.propertyContactId.value;
        return this.addContactToProperty(propertyContactId);
      }

      if (!this.$refs.contactForm.isValidated()) {
        return;
      }

      this.loading = true;
      const params = {};

      forEach(this.contact.form, (object, key) => {
        if (object.value && object.value.length > 0) {
          params[key] = object.value;
        }
      });

      try {
        const { data } = await PropertyContact.add(params);

        const title = 'New contact added';
        const text = `Contact # ${data.data.propertyContactId} successfully added`;
        const type = 'success';

        this.$notify({
          duration: 7000,
          title,
          text,
          type,
        });

        this.addContactToProperty(data.data.propertyContactId);
      } catch (e) {

      } finally {
        this.loading = false;
      }

      return true;
    },
    async addContactToProperty(propertyContactId) {
      this.loading = true;

      try {
        await Property.update(this.property.propertyId, { propertyContactId });

        const title = 'Contact assigned against property';
        const type = 'success';

        this.$notify({
          duration: 7000,
          title,
          type,
        });

        this.hideModal();
      } catch (e) {

      } finally {
        this.loading = false;
      }
    },
    populateFields(form, values) {
      forEach(values, (value, key) => {
        if (key === 'propertyContactId') {
          return true;
        }
        if (key in form) {
          this.$set(form[key], 'value', value);
        }
      });

      return form;
    },
    resetForm(form) {
      forEach(form, (object, key) => {
        form[key].value = null;
      });
    },
    nextStep() {
      this.step += 1;
    },
  },
};
</script>

<style lang="scss">
    #addPropertyModal {
        .modal-content {
            min-height: calc(100vh - 3.5rem);
        }
    }
    .modal-body-sm .modal .modal-body {
        padding-top: 1.5rem;
        padding-bottom: 1.5rem;
    }
</style>
